/**
 * SupporterModal Component
 *
 * Modal dialog explaining the supporter program and providing
 * checkout/management options.
 *
 * For free users: Shows benefits and CTA to start trial
 * For supporters: Shows status and link to manage subscription
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSupporter } from '../contexts/SupporterContext';
import { useModalChrome } from '../hooks/useModalChrome';
import { trackSupporterModalOpen, trackSupporterCheckoutStart, trackSupporterPortalOpen, trackEvent } from '../utils/analytics';
import './SupporterModal.css';

interface SupporterModalProps {
  onClose: () => void;
  /** Which gate opened this modal, for conversion-funnel attribution. */
  source?: string;
}

interface SupporterFeature {
  icon: string;
  title: string;
  desc: string;
}

// Features live TODAY for supporters. Keep this honest — it's what someone
// actually gets the moment they subscribe.
const SUPPORTER_FEATURES_AVAILABLE: SupporterFeature[] = [
  {
    icon: '\u{1F5FA}\u{FE0F}', // 🗺️
    title: 'HD imagery (NSW)',
    desc: '10–50 cm aerial imagery. Zoom right in to pick out individual buildings, sheds, fire trails and access roads across NSW — in both 2D and 3D.',
  },
  {
    icon: '\u{1F6F0}\u{FE0F}', // 🛰️
    title: 'Satellite imagery',
    desc: 'Recent true-colour satellite views of the whole country — watch smoke plumes and fresh burn scars from space.',
  },
  {
    icon: '\u{2708}\u{FE0F}', // ✈️
    title: 'Aircraft tracking',
    desc: 'Live water-bombers and helicopters via ADS-B. Aerial activity is one of the clearest signs a fire is being actively fought.',
  },
  {
    icon: '\u{1F32C}\u{FE0F}', // 🌬️
    title: '3D wind analysis',
    desc: 'Wind arrows and terrain-corrected wind on the 3D globe — see how ridges and valleys will push a fire.',
  },
];

// Genuinely still in development. Marked clearly so nobody feels misled.
const SUPPORTER_FEATURES_UPCOMING: SupporterFeature[] = [
  {
    icon: '\u{1F525}', // 🔥
    title: 'Fire-spread prediction',
    desc: 'Modelled estimates of where a fire is likely to head next, from wind, terrain and fuel.',
  },
  {
    icon: '\u{1F4E1}', // 📡
    title: 'On-demand satellite',
    desc: 'Request the latest high-resolution satellite pass over your area.',
  },
];

function SupporterFeatureList({
  features,
  badge,
  badgeClass,
}: {
  features: SupporterFeature[];
  badge: string;
  badgeClass: string;
}) {
  return (
    <ul>
      {features.map((f) => (
        <li key={f.title}>
          <span className="feature-icon">{f.icon}</span>
          <div className="feature-text">
            <span className="feature-title">{f.title}</span>
            <span className="feature-desc">{f.desc}</span>
          </div>
          <span className={`feature-badge ${badgeClass}`}>{badge}</span>
        </li>
      ))}
    </ul>
  );
}

export default function SupporterModal({ onClose, source = 'unknown' }: SupporterModalProps) {
  const { isAuthenticated } = useAuth();
  const {
    status,
    trialEndsAt,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    isSupporter,
    startCheckout,
    openPortal,
  } = useSupporter();

  // Conversion funnel: record which gate opened the modal (once, on mount).
  useEffect(() => {
    trackSupporterModalOpen(source);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Friendly (non-error) notice, e.g. when there's no Stripe subscription to
  // manage for a comp/admin-granted supporter.
  const [portalNotice, setPortalNotice] = useState<string | null>(null);

  // Shared modal chrome: document-level Escape (the old overlay onKeyDown only
  // fired when the overlay itself held focus, which nothing set — so Escape
  // usually did nothing), body scroll-lock (background map no longer scrolls
  // behind the modal on touch), and a focus trap.
  const modalRef = useRef<HTMLDivElement>(null);
  useModalChrome({ isOpen: true, onClose, modalRef });

  // Format date for display
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Logged-out CTA: close this modal and ask the app to open the auth flow.
  // Decoupled via a window event so it works no matter which gate opened the
  // modal (App listens and shows the AuthModal).
  const handleRequestAuth = () => {
    trackEvent('supporter_login_cta', { source });
    // Remember to re-open the supporter modal once they're authenticated, so the
    // flow is seamless. sessionStorage (not just a callback) survives the OAuth
    // redirect that social login performs.
    try { sessionStorage.setItem('firewatch-reopen-supporter', source || '1'); } catch { /* private mode */ }
    window.dispatchEvent(new CustomEvent('firewatch:request-auth'));
    onClose();
  };

  const handleStartCheckout = async () => {
    setIsLoading(true);
    setError(null);
    trackSupporterCheckoutStart(source);
    try {
      await startCheckout();
      // Will redirect to Stripe
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setIsLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    setIsLoading(true);
    setError(null);
    setPortalNotice(null);
    trackSupporterPortalOpen();
    try {
      await openPortal();
      // Will redirect to Stripe portal
    } catch (err) {
      // The billing portal only exists for card-based Stripe subscriptions.
      // Comp / admin-granted supporters have no Stripe customer, so the API
      // returns "Failed to create portal session". Show a friendly notice
      // rather than a scary error. (user feedback)
      setPortalNotice(
        "Your supporter access was granted directly, so there's no card subscription to manage here — you're all set. 💚 If you subscribed with a card and expected the billing portal, please try again shortly."
      );
      setIsLoading(false);
    }
  };

  // Close on backdrop click — but ONLY when the press both starts AND ends on
  // the backdrop itself. Without tracking the press origin, dragging to scroll
  // the (tall) modal content and releasing over the backdrop fired a click
  // whose target was the overlay, closing the modal mid-scroll. (user bug)
  const pressStartedOnBackdrop = useRef(false);

  const handleOverlayPointerDown = (e: React.PointerEvent) => {
    pressStartedOnBackdrop.current = e.target === e.currentTarget;
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (pressStartedOnBackdrop.current && e.target === e.currentTarget) {
      onClose();
    }
    pressStartedOnBackdrop.current = false;
  };

  // Use portal to render at document body level to escape stacking contexts
  // This ensures the modal appears above mobile navigation, sidebars, and 3D viewer widgets
  return createPortal(
    <div
      className="supporter-modal-overlay"
      onPointerDown={handleOverlayPointerDown}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="supporter-modal-title"
    >
      <div className="supporter-modal" ref={modalRef}>
        {/* Close button */}
        <button
          className="supporter-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>

        {/* Header */}
        <div className="supporter-modal-header">
          <span className="supporter-modal-heart">&#x2764;</span>
          <h2 id="supporter-modal-title">
            {isSupporter ? 'Thank You for Your Support!' : 'Help Keep Hipponics Firewatch Running'}
          </h2>
        </div>

        {/* Content - varies based on subscription status */}
        <div className="supporter-modal-content">
          {!isAuthenticated ? (
            // Logged-out: sell the value FIRST, then ask them to sign in. A bare
            // "please log in" converts far worse than showing what they'd get.
            <>
              <p className="supporter-modal-message">
                Hipponics Firewatch is a free, ad-free community tool that helps
                Australians stay safe during bushfire season. Supporters keep it
                running for everyone and unlock extra tools. Create a free account
                (or log in) to become a supporter.
              </p>

              <div className="supporter-modal-price">
                <span className="supporter-modal-price-amount">$19</span>
                <span className="supporter-modal-price-period">/year</span>
                <span className="supporter-modal-price-trial">3-day free trial · cancel anytime</span>
              </div>

              <div className="supporter-modal-features">
                <h3>What your support unlocks</h3>
                <div className="supporter-modal-feature-group-label">Available now</div>
                <SupporterFeatureList
                  features={SUPPORTER_FEATURES_AVAILABLE}
                  badge="Included"
                  badgeClass="available"
                />
                <div className="supporter-modal-feature-group-label">In development</div>
                <SupporterFeatureList
                  features={SUPPORTER_FEATURES_UPCOMING}
                  badge="Coming Soon"
                  badgeClass=""
                />
              </div>

              <div className="supporter-modal-why">
                <h3>Why it needs supporters</h3>
                <ul className="supporter-modal-promises">
                  <li>Keeps Firewatch free &amp; ad-free for everyone</li>
                  <li>We never sell your data or run ads</li>
                  <li>Directly funds the live data feeds and hosting</li>
                  <li>Cancel anytime — no lock-in</li>
                </ul>
              </div>

              <button
                className="supporter-modal-button primary"
                onClick={handleRequestAuth}
              >
                Log in or sign up to continue
              </button>
            </>
          ) : isSupporter ? (
            // Active supporter view
            <>
              <p className="supporter-modal-message">
                Your support helps cover hosting costs and enables us to keep
                improving Hipponics Firewatch for everyone.
              </p>

              <div className="supporter-modal-status">
                <div className="supporter-modal-status-row">
                  <span className="supporter-modal-status-label">Status:</span>
                  <span className={`supporter-modal-status-value status-${status}`}>
                    {status === 'trialing' ? 'Free Trial' : 'Active'}
                    {cancelAtPeriodEnd && ' (Canceling)'}
                  </span>
                </div>

                {status === 'trialing' && trialEndsAt && (
                  <div className="supporter-modal-status-row">
                    <span className="supporter-modal-status-label">Trial ends:</span>
                    <span className="supporter-modal-status-value">
                      {formatDate(trialEndsAt)}
                    </span>
                  </div>
                )}

                {currentPeriodEnd && (
                  <div className="supporter-modal-status-row">
                    <span className="supporter-modal-status-label">
                      {cancelAtPeriodEnd ? 'Access until:' : 'Renews:'}
                    </span>
                    <span className="supporter-modal-status-value">
                      {formatDate(currentPeriodEnd)}
                    </span>
                  </div>
                )}
              </div>

              <div className="supporter-modal-features">
                <h3>What you have access to</h3>
                <div className="supporter-modal-feature-group-label">Available now</div>
                <SupporterFeatureList
                  features={SUPPORTER_FEATURES_AVAILABLE}
                  badge="Included"
                  badgeClass="available"
                />
                <div className="supporter-modal-feature-group-label">In development</div>
                <SupporterFeatureList
                  features={SUPPORTER_FEATURES_UPCOMING}
                  badge="Coming Soon"
                  badgeClass=""
                />
              </div>

              <button
                className="supporter-modal-button secondary"
                onClick={handleOpenPortal}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Manage Subscription'}
              </button>
            </>
          ) : (
            // Free user / conversion view
            <>
              <p className="supporter-modal-message">
                Hipponics Firewatch is a free, ad-free community tool that helps
                Australians stay safe during bushfire season. It's run by a small
                team and paid for out of pocket — live fire feeds, satellite data,
                maps and hosting all cost money. Supporters keep it running for
                everyone, and unlock extra tools that give you a sharper picture
                when it matters.
              </p>

              <div className="supporter-modal-price">
                <span className="supporter-modal-price-amount">$19</span>
                <span className="supporter-modal-price-period">/year</span>
                <span className="supporter-modal-price-trial">3-day free trial · cancel anytime</span>
              </div>

              <div className="supporter-modal-features">
                <h3>What your support unlocks</h3>
                <div className="supporter-modal-feature-group-label">Available now</div>
                <SupporterFeatureList
                  features={SUPPORTER_FEATURES_AVAILABLE}
                  badge="Included"
                  badgeClass="available"
                />
                <div className="supporter-modal-feature-group-label">In development</div>
                <SupporterFeatureList
                  features={SUPPORTER_FEATURES_UPCOMING}
                  badge="Coming Soon"
                  badgeClass=""
                />
              </div>

              <div className="supporter-modal-why">
                <h3>Why it needs supporters</h3>
                <ul className="supporter-modal-promises">
                  <li>Keeps Firewatch free &amp; ad-free for everyone</li>
                  <li>We never sell your data or run ads</li>
                  <li>Directly funds the live data feeds and hosting</li>
                  <li>Cancel anytime — no lock-in</li>
                </ul>
              </div>

              <button
                className="supporter-modal-button primary"
                onClick={handleStartCheckout}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Start Free Trial'}
              </button>
            </>
          )}

          {error && (
            <div className="supporter-modal-error">
              {error}
            </div>
          )}

          {portalNotice && (
            <div className="supporter-modal-notice">
              {portalNotice}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
